import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowLeft, Printer } from 'lucide-react';
import BlurredValue from '../../../components/common/BlurredValue';
import { formatDateToISO } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleTrialBalancePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [trialBalance, setTrialBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [asOfDate, setAsOfDate] = useState(formatDateToISO(new Date()));

  useEffect(() => {
    fetchTrialBalance();
  }, [asOfDate]);

  const fetchTrialBalance = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/gl/accounts/trial-balance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { as_of_date: asOfDate, module: 'wholesale' }
      });
      setTrialBalance(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch wholesale trial balance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const groupByType = () => {
    if (!trialBalance?.accounts) return {};

    const grouped = {
      asset: [],
      liability: [],
      equity: [],
      revenue: [],
      expense: [],
      cogs: []
    };

    trialBalance.accounts.forEach((account) => {
      const type = account.account_type;
      if (grouped[type]) {
        grouped[type].push(account);
      }
    });

    return grouped;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const groupedAccounts = groupByType();

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/wholesale/reports')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Wholesale Trial Balance</h1>
            <p className="text-sm text-gray-500">General ledger trial balance - Wholesale Module</p>
          </div>
        </div>
        <div className="flex gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">As of Date</label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handlePrint}
            className="self-end text-white px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold text-gray-900 text-center">Wholesale Trial Balance</h1>
        <p className="text-sm text-gray-600 text-center">As of {formatDate(asOfDate)}</p>
        <p className="text-sm text-gray-600 text-center">Wholesale Module Report</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 print:hidden">
          {error}
        </div>
      )}

      {trialBalance && (
        <>
          {/* Balance Check */}
          {trialBalance.totals && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm text-gray-500">Total Debits</div>
                  <div className="text-xl font-bold text-gray-900">
                    <BlurredValue value={formatCurrency(trialBalance.totals.total_debits)} />
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-500">Difference</div>
                  <div
                    className={`text-xl font-bold ${trialBalance.totals.is_balanced ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {trialBalance.totals.is_balanced ? '✓ Balanced' : '✗ Out of Balance'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Total Credits</div>
                  <div className="text-xl font-bold text-gray-900">
                    <BlurredValue value={formatCurrency(trialBalance.totals.total_credits)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trial Balance Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Account Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Account Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Debit
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Credit
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {/* Assets */}
                  {groupedAccounts.asset && groupedAccounts.asset.length > 0 && (
                    <>
                      <tr className="bg-emerald-50">
                        <td colSpan="4" className="px-6 py-2 text-sm font-bold text-gray-900">
                          ASSETS
                        </td>
                      </tr>
                      {groupedAccounts.asset.map((account) => {
                        const balance = parseFloat(account.balance) || 0;
                        return (
                          <tr key={account.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {account.account_code}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900">
                              {account.account_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance > 0 ? formatCurrency(balance) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance < 0 ? formatCurrency(Math.abs(balance)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* Liabilities */}
                  {groupedAccounts.liability && groupedAccounts.liability.length > 0 && (
                    <>
                      <tr className="bg-gray-100">
                        <td colSpan="4" className="px-6 py-2 text-sm font-bold text-gray-900">
                          LIABILITIES
                        </td>
                      </tr>
                      {groupedAccounts.liability.map((account) => {
                        const balance = parseFloat(account.balance) || 0;
                        return (
                          <tr key={account.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {account.account_code}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900">
                              {account.account_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance > 0 ? formatCurrency(balance) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance < 0 ? formatCurrency(Math.abs(balance)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* Equity */}
                  {groupedAccounts.equity && groupedAccounts.equity.length > 0 && (
                    <>
                      <tr className="bg-gray-100">
                        <td colSpan="4" className="px-6 py-2 text-sm font-bold text-gray-900">
                          EQUITY
                        </td>
                      </tr>
                      {groupedAccounts.equity.map((account) => {
                        const balance = parseFloat(account.balance) || 0;
                        return (
                          <tr key={account.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {account.account_code}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900">
                              {account.account_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance > 0 ? formatCurrency(balance) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance < 0 ? formatCurrency(Math.abs(balance)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* Revenue */}
                  {groupedAccounts.revenue && groupedAccounts.revenue.length > 0 && (
                    <>
                      <tr className="bg-blue-50">
                        <td colSpan="4" className="px-6 py-2 text-sm font-bold text-gray-900">
                          REVENUE
                        </td>
                      </tr>
                      {groupedAccounts.revenue.map((account) => {
                        const balance = parseFloat(account.balance) || 0;
                        return (
                          <tr key={account.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {account.account_code}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900">
                              {account.account_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance > 0 ? formatCurrency(balance) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance < 0 ? formatCurrency(Math.abs(balance)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* COGS */}
                  {groupedAccounts.cogs && groupedAccounts.cogs.length > 0 && (
                    <>
                      <tr className="bg-orange-50">
                        <td colSpan="4" className="px-6 py-2 text-sm font-bold text-gray-900">
                          COST OF GOODS SOLD
                        </td>
                      </tr>
                      {groupedAccounts.cogs.map((account) => {
                        const balance = parseFloat(account.balance) || 0;
                        return (
                          <tr key={account.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {account.account_code}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900">
                              {account.account_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance > 0 ? formatCurrency(balance) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance < 0 ? formatCurrency(Math.abs(balance)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* Expenses */}
                  {groupedAccounts.expense && groupedAccounts.expense.length > 0 && (
                    <>
                      <tr className="bg-red-50">
                        <td colSpan="4" className="px-6 py-2 text-sm font-bold text-gray-900">
                          EXPENSES
                        </td>
                      </tr>
                      {groupedAccounts.expense.map((account) => {
                        const balance = parseFloat(account.balance) || 0;
                        return (
                          <tr key={account.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {account.account_code}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-900">
                              {account.account_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance > 0 ? formatCurrency(balance) : '-'}
                            </td>
                            <td className="px-6 py-3 text-sm text-right text-gray-900">
                              {balance < 0 ? formatCurrency(Math.abs(balance)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}

                  {/* Totals */}
                  {trialBalance.totals && (
                    <tr className="bg-gray-50 font-bold">
                      <td colSpan="2" className="px-6 py-4 text-sm text-gray-900">
                        TOTAL
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-emerald-600">
                        <BlurredValue value={formatCurrency(trialBalance.totals.total_debits)} />
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-emerald-600">
                        <BlurredValue value={formatCurrency(trialBalance.totals.total_credits)} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Report Footer */}
          <div className="mt-6 text-sm text-gray-500 print:block">
            <p>Generated on {new Date().toLocaleString('en-NG')}</p>
            <p>Report run by: {user?.email}</p>
            <p className="text-emerald-600 font-medium">Wholesale Module Report</p>
          </div>
        </>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .p-4, .p-4 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WholesaleTrialBalancePage;
